// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialStudentTransportDetailState,
    StudentTransportDetailSchema,
    StudentTransportDetailFormFields,
} from "./Configuration.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import GoDialog from "../../../common/GoDialog/GoDialog";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";

const StudentTransportDetail = ({ id, studentId, schoolId, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [student_transport_detail, setStudentTransportDetail] = useState(InitialStudentTransportDetailState);
    const [formFields, setFormFields] = useState(StudentTransportDetailFormFields);
    const [confirm, setConfirm] = useState(false);
    const [currentValues, setCurrentValues] = useState(null);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && field.type === "select"
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const handleBusDetailChange = async () => {
        const response = await get(EntityConstants.BUS_DETAILS, "max=all");
        const data = await response.json();
        attachOptions("morning_bus_id", data);
        attachOptions("evening_bus_id", data);
        spinService.off();
    };

    const handleTransportTypeChange = async () => {
        const response = await get(EntityConstants.TRANSPORT_TYPES, "max=all");
        const data = await response.json();
        attachOptions("transport_type_id", data);
        spinService.off();
    };

    const handleZoneChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.ZONES_BY_SCHOOL + `/${schoolId}`);
        const data = await response.json();
        attachOptions("zone_id", data);
        spinService.off();
    };

    const handleAreaChange = async (zone_id) => {
        spinService.on();
        const response = await get(EntityConstants.AREAS_BY_ZONE + `/${zone_id}`);
        const data = await response.json();
        attachOptions("area_id", data);
        spinService.off();
    };

    const handleCustomChange = (e, values) => {
        if (e.target.name === "status" && values.id !== undefined && !student_transport_detail.status) {
            if (e.target.value === "false") {
                toast.error("Once Invoice is Inactivated we cannot activate it again");
            }
        }
        if (e.target.name === "school_id") {
            handleZoneChange(e.target.value);
        }
        if (e.target.name === "zone_id") {
            handleAreaChange(e.target.value);
        }
    };

    const getStudentTransportDetail = async (id) => {
        const response = await get(EntityConstants.STUDENT_TRANSPORT_DETAILS + `/${id}`);
        const data = await response.json();
        setStudentTransportDetail(data);
        handleZoneChange(data.school_id);
        handleAreaChange(data.zone_id);
        spinService.off();
    };

    const handleSubmit = async (values) => {
        spinService.on();
        values.student_id = studentId;
        const isNewStudentTransportDetail = values.id === undefined;
        const url = isNewStudentTransportDetail ? EntityConstants.STUDENT_TRANSPORT_DETAILS : EntityConstants.STUDENT_TRANSPORT_DETAILS + `/${values.id}`;
        const method = isNewStudentTransportDetail ? post : put;
        const response = await method(url, { student_transport_detail: values });
        if ([200, 201].indexOf(response.status) > -1) {
            toast.success(isNewStudentTransportDetail ? MessageContants.STUDENT_TRANSPORT_DETAIL_CREATED : MessageContants.STUDENT_TRANSPORT_DETAIL_UPDATED);
            onClose();
        }
        spinService.off();
    };

    const handleConfirm = async (values) => {
        if (values.id === undefined) {
            setCurrentValues(values);
            setConfirm(true);
        } else {
            handleSubmit(values);
        }
    }

    const handleClose = (res) => {
        if (res) {
            handleSubmit(currentValues);
        } else {
            setConfirm(false);
        }
    };

    useEffect(() => {
        handleTransportTypeChange();
        handleZoneChange();
        handleBusDetailChange();
        if (id !== undefined && id !== null) {
            getStudentTransportDetail(Number(id));
        }
    }, [id]);

    return (
        <React.Fragment>
            <Formik
                enableReinitialize
                initialValues={student_transport_detail}
                validationSchema={StudentTransportDetailSchema}
                onSubmit={(values) => handleConfirm(values)}
            >
                {({
                    values,
                    touched,
                    errors,
                    dirty,
                    isSubmitting,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                maxWidth="sm"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={(e) => { handleChange(e); handleCustomChange(e, values); }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {((values.id === undefined && permissions.has(PermissionConstants.STUDENT_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.STUDENT_UPDATE) && student_transport_detail.status)) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:save-fill" />}
                                        type="submit"
                                    >
                                        {values.id === undefined ? 'Save' : 'Update'}
                                    </Button>
                                }
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <GoDialog
                open={confirm}
                title=""
                content="Are you sure you want to save the details"
                positive="Yes"
                negative="Cancel"
                handleClose={(res) => handleClose(res)}
            />
        </React.Fragment>
    );
};

StudentTransportDetail.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default StudentTransportDetail;
